import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/dentlab/src/components/ui/dialog";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import { DateTimePicker } from "@/dentlab/src/components/ui/datetime-picker";
import {
	ToggleGroup,
	ToggleGroupItem,
} from "@/dentlab/src/components/ui/toggle-group";
import { useTeamProfilesContext } from "@/dentlab/src/context/single-entity-contexts/TeamProfilesContext";
import {
	CourierTypeEnum,
	CreateDeliveryParams,
} from "@/lib/types/delivery.types";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/dentlab/src/components/ui/select";

interface CreateDeliveryDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	jobId: number;
	onSubmit: (params: CreateDeliveryParams) => Promise<void>;
}

export const CreateDeliveryDialog: React.FC<CreateDeliveryDialogProps> = ({
	open,
	onOpenChange,
	jobId,
	onSubmit,
}) => {
	const {
		teamProfiles: { teamProfiles },
	} = useTeamProfilesContext();
	const [courierType, setCourierType] = useState<CourierTypeEnum>(
		CourierTypeEnum.PERSON
	);
	const [profileId, setProfileId] = useState<string>("");
	const [companyName, setCompanyName] = useState("");
	const [description, setDescription] = useState("");
	const [scheduledDatetime, setScheduledDatetime] = useState<
		Date | undefined
	>();
	const [isFinalDelivery, setIsFinalDelivery] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const activeProfiles = teamProfiles.filter(
		(p) =>
			!p.deactivated_at &&
			!(
				p.email?.includes("support-") &&
				p.email?.includes("@dentdesk.ch")
			)
	);

	const handleSubmit = async () => {
		setSubmitting(true);
		let departureIso: string | undefined;
		if (scheduledDatetime) {
			departureIso = scheduledDatetime.toISOString();
		}
		await onSubmit({
			job_id: jobId,
			courier_type: courierType,
			profile_id:
				courierType === CourierTypeEnum.PERSON && profileId
					? profileId
					: undefined,
			courier_company_name:
				courierType === CourierTypeEnum.COMPANY
					? companyName
					: undefined,
			description: description || undefined,
			scheduled_departure_at: departureIso,
			is_final_delivery: isFinalDelivery,
		});
		setSubmitting(false);
		resetForm();
		onOpenChange(false);
	};

	const resetForm = () => {
		setCourierType(CourierTypeEnum.PERSON);
		setProfileId("");
		setCompanyName("");
		setDescription("");
		setScheduledDatetime(undefined);
		setIsFinalDelivery(true);
	};

	const isValid =
		courierType === CourierTypeEnum.PERSON
			? !!profileId
			: !!companyName.trim();

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) resetForm();
				onOpenChange(o);
			}}
		>
			<DialogContent data-testid="delivery-create-dialog">
				<DialogHeader>
					<DialogTitle>Neue Lieferung</DialogTitle>
					<DialogDescription>
						Erstellen Sie eine neue Lieferung für diesen Auftrag.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 pt-2">
					<div>
						<Label className="text-sm font-medium mb-1.5 block">
							Kurierart
						</Label>
						<ToggleGroup
							type="single"
							value={courierType}
							onValueChange={(v) =>
								v && setCourierType(v as CourierTypeEnum)
							}
							className="justify-start"
							data-testid="delivery-courier-type-toggle"
						>
							<ToggleGroupItem
								value={CourierTypeEnum.PERSON}
								className="text-xs"
							>
								Person
							</ToggleGroupItem>
							<ToggleGroupItem
								value={CourierTypeEnum.COMPANY}
								className="text-xs"
							>
								Unternehmen
							</ToggleGroupItem>
						</ToggleGroup>
					</div>

					{courierType === CourierTypeEnum.PERSON ? (
						<div>
							<Label className="text-sm font-medium mb-1.5 block">
								Kurier
							</Label>
							<Select
								value={profileId}
								onValueChange={setProfileId}
							>
								<SelectTrigger data-testid="delivery-courier-select">
									<SelectValue placeholder="Person auswählen" />
								</SelectTrigger>
								<SelectContent>
									{activeProfiles.map((profile) => (
										<SelectItem
											key={profile.id}
											value={profile.id}
										>
											{profile.first_name}{" "}
											{profile.last_name}
											{profile.code
												? ` (${profile.code})`
												: ""}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					) : (
						<div>
							<Label className="text-sm font-medium mb-1.5 block">
								Unternehmen
							</Label>
							<Input
								value={companyName}
								onChange={(e) => setCompanyName(e.target.value)}
								placeholder="z.B. DHL, Post"
								data-testid="delivery-company-name-input"
							/>
						</div>
					)}

					<div>
						<Label className="text-sm font-medium mb-1.5 block">
							Beschreibung
						</Label>
						<Textarea
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Optionale Beschreibung"
							rows={2}
							data-testid="delivery-description-input"
						/>
					</div>

					<div>
						<Label className="text-sm font-medium mb-1.5 block">
							Geplante Abfahrt
						</Label>
						<DateTimePicker
							value={scheduledDatetime}
							onChange={setScheduledDatetime}
							data-testid="delivery-scheduled-datetime-input"
							aria-label="Geplante Abfahrt Datum und Uhrzeit"
						/>
					</div>

					<div className="flex items-center gap-2">
						<Checkbox
							id="is-final-delivery"
							checked={isFinalDelivery}
							onCheckedChange={(checked) =>
								setIsFinalDelivery(checked === true)
							}
							data-testid="delivery-final-checkbox"
							aria-label="Endlieferung"
						/>
						<Label
							htmlFor="is-final-delivery"
							className="text-sm cursor-pointer"
							title="Setzt das Ausgangsdatum des Auftrags auf das geplante Abgangsdatum dieser Lieferung"
						>
							Endlieferung
						</Label>
					</div>

					<div className="flex justify-end gap-2 pt-2">
						<Button
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
							disabled={submitting}
						>
							Abbrechen
						</Button>
						<Button
							onClick={handleSubmit}
							disabled={!isValid || submitting}
						>
							{submitting ? "Erstelle..." : "Erstellen"}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
